import { Component, ElementRef, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalDirective } from '@shared/directives/modal.directive';
import { ICompany } from '@shared/interfaces/company.interface';
import { IContact } from '@shared/interfaces/contact.interface';
import { INotification } from '@shared/interfaces/notification.interface';
import { ILocation } from '@shared/interfaces/location.interface';
import { ContactsService } from '@shared/services/contacts.service';
import { NotificationsService } from '@shared/services/notifications.service';
import { LocationsService } from '@shared/services/locations.service';
import { LoggerService } from '@shared/services/logger.service';
import { ToastService } from '@shared/services/toast.service';
import { DomSanitizer } from '@angular/platform-browser';
import { LibrariesService } from '@shared/services/libraries.service';
import { IPayload } from '@shared/interfaces/payload.interface';
import jwtDecode from 'jwt-decode';
import { CommonEnvironmentsService } from '@shared/services/environments.service';
import { NgxSelectOption } from 'ngx-select-ex/ngx-select/ngx-select.classes';

@Component({
  selector: 'app-modals-notifications',
  templateUrl: './modals.notifications.component.html',
  styleUrls: ['./modals.notifications.component.scss']

})
export class ModalsNotificationsComponent
  extends ModalDirective<INotification>
  implements OnInit {
  public title = String('NOTIFICATIONS.ADD.TITLE');
  public description = String('NOTIFICATIONS.ADD.DESCRIPTION');
  public config: any = {
    class: 'modal-lg'
  };
  public customMenu: ElementRef;
  public fileInput: ElementRef<HTMLInputElement>;
  public disableButtons = Boolean(false);
  public isEditing = Boolean(false);
  public isOpen = Boolean(false);
  public submitKey = String('BUTTONS.SUBMIT');
  public deleteKey: string;
  public isLoading = Boolean(false);
  public locations: Partial<ILocation>[] = [];
  public contacts: Partial<IContact>[] = [];
  public subLocations: Partial<ILocation>[] = [];
  public libraryList: any[] = [];
  public subLocationsFilters: any[] = [
    {
      key: 'pagination',
      value: false
    }
  ];

  public entry: FormGroup;
  public fgSelected: FormGroup;
  public userId: string;
  private readonly constructorName: string = String(this.constructor.name);

  constructor(
    private readonly _notifications: NotificationsService,
    private readonly _logger: LoggerService,
    private readonly _fb: FormBuilder,
    private readonly _toasts: ToastService,
    public readonly _locations: LocationsService,
    private readonly _contacts: ContactsService,
    public readonly _libraries: LibrariesService,
    private readonly _commonEnvironments: CommonEnvironmentsService,
    public dom: DomSanitizer
  ) {
    super();
    this.onGetValueFromAttributes = this.onGetValueFromAttributes.bind(this);
    this.onGetValueFromAttributesLocation = this.onGetValueFromAttributesLocation.bind(this);
    this.onMatOptionSelectSubLocation = this.onMatOptionSelectSubLocation.bind(this);

  }

  ngOnInit(): void {
    const payload: IPayload = jwtDecode(this._commonEnvironments.getToken());
    this.userId = payload.id;
    this.openModal.subscribe(() => {
      this.isOpen = false;
      this.deleteKey = null;
      this.getContacts();
      this.getParentLocations();

    });
    this.createForm();
  }

  ngOnDestroy() {
  }


  public whenModalClose(type: string): void {
    if (type === 'SUBMIT') {
      this.errors = [];
      this.entityForm.ngSubmit.emit();
    } 
  }

  public onSubmit({ value, valid }: { value: any; valid: boolean }): void {
    if (valid) {
      let method = 'post';
      let url = `${method.toUpperCase()} /notifications`;
      this.entry.disable(); 
      this.disableButtons = true;
      value.to = value.occupants;
      const emailsList = this.contacts.map(item => item.email);
      value.to = (value.occupants[0] === 'All Occupants') ? emailsList : value.occupants;
      this._notifications[method](value).subscribe(
        (res: INotification) => {
          this._logger.info(this.constructorName, url, res);
          this._toasts.success('Notification sent!');
          this.entry.enable();
          this.disableButtons = false;
          this.resetModal();
          this.setDefaultValues();
          this.resetForm();
          this.closeModal.next();
        },
        (err: any) => {
          this._logger.error(this.constructorName, url, err);
          if (err.errors) {
            this.errors = err.errors;
          }
          this.entry.enable();
          this.disableButtons = false;
          this._toasts.error('An error has occurred!');
        }
      );
        
    }
  }


  public onGetValueFromAttributes(e: ICompany) {
    return e.name;
  }

  public onDismiss() {
    this.errors = [];
    this.entry?.reset();
    this.setDefaultValues();
  }

  private setDefaultValues() {
    this.disableButtons = false;
    this.entityForm.reset();
    this.errors = [];
    this.createForm();
    this.entry.enable();
  }


  protected createForm() {
    this.entry = this._fb.group({
      subject: ['', [Validators.required]],
      message: ['', [Validators.required]],
      occupants: [[], [Validators.required]],
      locations: [[], []],
      subLocations: [[], []],
    });
  }

  private getSubLocation() {

    if (this.entry.get('locations').value?.length > 0) {

      this.subLocationsFilters = [
        {
          key: 'parent',
          value: this.entry.get('locations').value[0] === '-1' ?
            this.locations.map(item => item.id.toString()).slice(1) : this.entry.get('locations').value.join(',')
        },
        {
          key: 'pagination',
          value: 'false'
        },
      ];
    }

    const url = `GET /locations`;
    this._locations.getSubLocations(1, this.subLocationsFilters).subscribe(
      (res: ILocation[]) => {
        this._logger.info(this.constructorName, url, res);

        this.subLocations = res.map((l: ILocation) => ({
          id: l.id,
          label: l.label
        }));

        this.subLocations.unshift({
          id: '-1',
          label: 'All Sub-Locations'
        });


        if (this.entry.get('subLocations')?.value?.length < 1) {
          this.entry.patchValue({ subLocations: ['-1'] });
        }

        setTimeout(() => {
          this.entry.patchValue({ occupants: ['All Occupants'] });
        }, 500);
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
      }
    );
  }

  private checkPattern(control: FormControl) {
    const patternRegex = new RegExp('/^[A-Za-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/');
    if (patternRegex.test(control.value)) {
      console.log('Match exists.');
    }
    else {
      return { pattern: true };
    }
  }

  private getParentLocations() {
    const locationsFilters: any[] = [
      {
        key: 'pagination',
        value: 'false'
      }
    ];

    const url = `GET /locations`;
    this._locations.get(1, locationsFilters).subscribe(
      (res: ILocation[]) => {
        this._logger.info(this.constructorName, url, res);

        this.locations = res.map((l: ILocation) => ({
          id: l.id,
          label: l.label
        }));
        this.locations.unshift({
          id: '-1',
          label: 'All Locations'
        });
        this.entry.patchValue({ locations: ['-1'] });
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
      }
    );
  }

  public getContacts() {
    this._contacts.get(1, [{ key: 'userType', value: 'TENANT' }, { key: 'pagination', value: 'false' }]).subscribe(
      (res: IContact[]) => {
        this.contacts = res;
        this.contacts.unshift({
          email: 'All Occupants',
        });
        this.isLoading = false;
      },
      (err: any) => {
        this.isLoading = false;
        console.log('err', err);
      }
    );
  }

  public selectChangesOccupant(e: any[]) {
    let occupants;
    if (e.length > 0 && e[e.length - 1].value === 'All Occupants') {
      occupants = ['All Occupants'];
    } else {
      occupants = e.filter(item => item.value !== 'All Occupants').map((ee: any) => ee.value);
    }
    setTimeout(() => {
      this.entry.patchValue({ occupants });
    }, 500);
  }

  public onGetValueFromAttributesLocation(e: ILocation) {
    return e.label;
  }

  public onMatOptionSelectSubLocation(e: NgxSelectOption[]): void {
    let subLocations;
    if (e.length > 0 && e[e.length - 1].value === '-1') {
      subLocations = ['-1'];
    } else {
      subLocations = e.filter(item => item.value !== '-1').map((ee: NgxSelectOption) => ee.value);
    }

    setTimeout(() => {
      if(e.length === 0) {
        this.entry.patchValue({ subLocations: [] });
        this.entry.patchValue({ occupants: [] });
        this.entry.patchValue({ locations: [] });
      } else {
        this.entry.patchValue({ subLocations });
      }
    }, 500);

  }

    public selectChangesLocations(e: NgxSelectOption[]) {

    let locations;
    if (e.length > 0 && e[e.length - 1].value === '-1') {
      locations = ['-1'];
    } else {
      locations = e.filter(item => item.value !== '-1').map((ee: NgxSelectOption) => ee.value);
    }

    setTimeout(() => {
      if(e.length === 0) {
        this.entry.patchValue({ locations: [] });
        this.entry.patchValue({ subLocations: [] });
        this.entry.patchValue({ occupants: [] });
      } else {
        this.entry.patchValue({ locations });
        this.getSubLocation();
      }

    }, 500);

  }


}